import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { Link } from "react-router-dom";
import { Filter, Plus } from "feather-icons-react/build/IconComponents";
import Swal from "sweetalert2";
import { all_routes } from "../../../Router/all_routes";
import httpService from "../../../services/http/peoples/company.service";
import usePagination, {
  createInitialPagination,
} from "../../../custom-hooks/usePagination";
import { useNavigate } from "react-router-dom";
import ConfirmationAlert from "../../../components/confirm-alert";
import UnlimitTable from "../../../components/ui-kit/unlimit-table";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import { useTranslation } from "react-i18next";
import useRoleAndPerm from "../../../utils/permissionFunction";
import { useDispatch } from "react-redux";
import { fetchCompanies } from "../../../core/redux/reducers/companies.slice";

const CompanyList = () => {
  const { hasPermission } = useRoleAndPerm();
  const { t } = useTranslation();
  const initialPagination = createInitialPagination(t);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [LmCompanyNo, setLmCompanyNo] = useState("");
  const [companyNameFilter, setCompanyNameFilter] = useState("");
  const dispatch = useDispatch();
  const { paginationParams, updatePaginationParams } =
    usePagination(initialPagination);
  const navigate = useNavigate();

  const getCompaniesList = async (
    pagination = paginationParams.pagination,
    filters,
    sorter = {}
  ) => {
    const data = {
      page: pagination?.current,
      per_page: pagination?.pageSize,
      ...filters,
      ...sorter,
    };
    setLoading(true);
    await httpService
      .companiesList(data)
      .then((response) => {
        if (response.status == 200) {
          setCompanies(response.data.data?.data);
        }
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
            current: response.data.data.current_page,
            total: response.data.data.total,
            pageSize: response.data.data.per_page,
          },
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    filters = {
      lm_company_number: LmCompanyNo,
      company_name: companyNameFilter,
    };
    const nonNullFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => !!value)
    );
    if (sorter && sorter.field) {
      sorter = {
        sort_by: sorter.field,
        sort_direction: sorter.order == "ascend" ? "ASC" : "DESC",
      };
    }
    if (!pagination) {
      pagination = initialPagination;
    }
    await updatePaginationParams({
      pagination,
      sorter,
    });
    await getCompaniesList(pagination, nonNullFilters, sorter);
  };

  useEffect(() => {
    getCompaniesList();
  }, []);

  const { companyCreate } = all_routes;

  const onConfirmDelete = async (id) => {
    await httpService
      .deleteCompany(id)
      .then((response) => {
        if (response.status == 200) {
          Swal.fire({
            title: t("general.prompts.deleted"),
            text: t("company.CompanyDeleteSuccess"),
            className: "btn btn-success",
            confirmButtonText: t("general.button.ok"),
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
          getCompaniesList();
          if (hasPermission(["company-view"])) dispatch(fetchCompanies(true));
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const columns = [
    {
      title: t("tableColumnName.srNo"),
      dataIndex: "index",
      align: "center",
      render: (value, item, index) =>
        (paginationParams?.pagination?.current - 1) *
          paginationParams?.pagination?.pageSize +
        (index + 1),
    },
    {
      title: t("tableColumnName.companyId"),
      dataIndex: "company_code",
      sorter: false,
    },
    {
      title: t("tableColumnName.lmCompanyNo"),
      dataIndex: "lm_company_number",
      sorter: false,
    },
    {
      title: t("tableColumnName.company"),
      dataIndex: "business_name",
      render: (text, record) => (
        <span className="productimgname">
          <Link
            to={`/company/${record.id}/view`}
            className="product-img stock-img"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <ImageWithBasePath
              alt={text}
              type="link"
              src={record.logo}
              style={{ width: 30, height: 30, marginRight: 10 }}
            />
            <span className="ms-2">{record.business_name}</span>
          </Link>
        </span>
      ),
    },
    {
      title: t("tableColumnName.adminName"),
      dataIndex: "admin_name",
      render: (text, record) => {
        return record?.user?.name;
      },
    },
    {
      title: t("tableColumnName.phone"),
      dataIndex: "phone",
    },
    {
      title: t("tableColumnName.email"),
      dataIndex: "email",
      sorter: false,
    },
    {
      title: t("tableColumnName.subAccount"),
      dataIndex: "company_accounts_count",
      render: (text, record) => <div>{record.company_accounts_count || 0}</div>,
    },
    {
      title: t("tableColumnName.employee"),
      dataIndex: "shoppers_count",
      render: (text, record) => <div>{record.shoppers_count || 0}</div>,
    },
    {
      title: t("tableColumnName.actions"),
      key: "actions",
      align: "center",
      hidden: !hasPermission([
        "company-view",
        "company-edit",
        "company-delete",
      ]),
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {hasPermission(["company-view"]) && (
              <Link
                to={`/company/${record.id}/view`}
                className="me-2 edit-icon p-2"
              >
                <i className="feather-eye"></i>
              </Link>
            )}
            {hasPermission(["company-edit"]) && (
              <a
                className="me-2 p-2"
                onClick={() => navigate(`/company/${record.id}/edit`)}
              >
                <i className="feather-edit-2"></i>
              </a>
            )}
            {hasPermission(["company-delete"]) && (
              <ConfirmationAlert onConfirm={() => onConfirmDelete(record.id)} />
            )}
          </div>
        </div>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: "", link: "/", icon: "feather-home" },
    { label: t("company.company"), active: true },
  ];
  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex flex-column">
              <div className="page-title">
                <h4>{t("company.companyList")}</h4>
              </div>
              <UnlimitBreadcrumb items={breadcrumbItems} />
            </div>
            {hasPermission(["company-add"]) && (
              <div className="page-btn">
                <Link to={companyCreate} className="btn btn-added">
                  <Plus className="me-2 iconsize" />
                  {t("general.button.addNew")}
                </Link>
              </div>
            )}
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top row">
                <div className="col-lg-5 col-sm-5 col-12">
                  <input
                    type="text"
                    placeholder={t("inputField.placeholder.enterLmCompanyNo")}
                    className="form-control form-control-sm formsearch-custom ps-2"
                    value={LmCompanyNo}
                    onChange={(e) => setLmCompanyNo(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleTableChange();
                      }
                    }}
                  />
                </div>
                <div className="col-lg-5 col-sm-5 col-12">
                  <input
                    type="text"
                    placeholder={t("inputField.placeholder.enterCompanyName")}
                    className="form-control form-control-sm formsearch-custom ps-2"
                    value={companyNameFilter}
                    onChange={(e) => setCompanyNameFilter(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleTableChange();
                      }
                    }}
                  />
                </div>
                <div className="col-lg-2 col-sm-6 col-12 ms-auto">
                  <div className="input-blocks mb-0">
                    <button
                      className="btn btn-filters col-auto ms-auto px-2"
                      onClick={handleTableChange}
                    >
                      <i data-feather="search" className="feather-search m-0" />{" "}
                    </button>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <UnlimitTable
                  pagination={paginationParams.pagination}
                  columns={columns}
                  dataSource={companies}
                  rowKey={(record) => record.id}
                  loading={loading}
                  onChange={handleTableChange}
                />
              </div>
            </div>
          </div>
          {/* /product list */}
        </div>
      </div>
    </div>
  );
};

export default CompanyList;
